import { useState } from "react";
import { api } from "../../lib/api";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Building2, ShieldCheck, Truck, Users, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  { icon: Truck, title: "Trade pricing", desc: "Tiered discounts for workshops, dealerships and recyclers" },
  { icon: ShieldCheck, title: "Net-30 terms", desc: "Approved accounts get monthly billing & dedicated rep" },
  { icon: Users, title: "Bulk fitment search", desc: "VIN-driven cataloguing, multi-vehicle quoting" },
];

export default function B2B() {
  const [form, setForm] = useState({
    company: "", abn: "", contact_name: "", email: "", phone: "",
    website: "", business_type: "", monthly_volume: "", notes: "",
  });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/b2b-requests", form);
      setDone(data);
      toast.success("Application received — we'll be in touch shortly");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="b2b-page">
      <StoreHeader />

      <section className="bg-[#0b1a3b] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#e31e24]">Trade · Wholesale · Workshops</div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase mt-3 leading-tight">Open a BCP trade account</h1>
            <p className="text-blue-100 mt-4 text-base md:text-lg max-w-prose">
              Fast-moving used &amp; new automotive parts at trade pricing. Built for workshops, smash repairers, and parts recyclers across Australia.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-1 gap-3">
            {BENEFITS.map((b) => (
              <div key={b.title} className="border-l-2 border-[#e31e24] bg-white/5 px-4 py-3 rounded-sm">
                <div className="flex items-center gap-2"><b.icon className="h-4 w-4 text-[#60a5fa]" /><div className="font-heading text-sm font-bold uppercase">{b.title}</div></div>
                <div className="text-blue-100 text-xs mt-1">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        {done ? (
          <div className="border border-green-500/30 bg-green-50 rounded-sm p-8 text-center" data-testid="b2b-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-600" />
            <div className="font-heading text-2xl font-bold uppercase text-[#0b1a3b] mt-3">Application received</div>
            <div className="text-zinc-600 mt-2">
              Reference <b className="font-mono">{done.ref}</b>. We'll review your application and respond within 1 business day.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-zinc-200 rounded-sm p-6 md:p-8 bg-white shadow-sm space-y-4" data-testid="b2b-form">
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="h-5 w-5 text-[#1e5bb8]" />
              <div className="font-heading text-2xl font-bold uppercase text-[#0b1a3b]">Trade application</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Company name *</Label>
                <Input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 rounded-sm" data-testid="b2b-company" />
              </div>
              <div>
                <Label>ABN</Label>
                <Input value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} className="mt-1 rounded-sm" data-testid="b2b-abn" />
              </div>
              <div>
                <Label>Contact name *</Label>
                <Input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 rounded-sm" data-testid="b2b-contact" />
              </div>
              <div>
                <Label>Email *</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" data-testid="b2b-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="b2b-phone" />
              </div>
              <div>
                <Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label>Business type</Label>
                <Input value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} className="mt-1 rounded-sm" placeholder="Workshop / Smash repairer / Recycler / Reseller" />
              </div>
              <div>
                <Label>Estimated monthly volume</Label>
                <Input value={form.monthly_volume} onChange={(e) => setForm({ ...form, monthly_volume: e.target.value })} className="mt-1 rounded-sm" placeholder="$5K–$10K / 50–100 parts / etc." />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-sm" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-sm bcp-gradient-red font-heading font-bold uppercase tracking-wide" data-testid="b2b-submit">
              {busy ? "Submitting…" : "Submit application"}
            </Button>
            <div className="text-xs text-zinc-500">Reviewed within 1 business day. Approval grants trade pricing &amp; net-30 terms.</div>
          </form>
        )}
      </section>

      <StoreFooter />
    </div>
  );
}
